import { ThunkAction, Action } from '@reduxjs/toolkit';
import { selectIsProjectEditable, selectProjectId, selectProjectVersion } from '../../../editor/selectors';
import { showNotification } from '../../../notifications/actions';
import listAppProjectPatchHistoryItemsSince from '../remote/listAppProjectPatchHistoryItemsSince';
import { rebaseEditorProject } from '../../../editor/actions';
import type { AppProjectEditorRootState } from '../../reducer';
import revertAppProjectIfInvalid from './revertAppProjectIfInvalid';

export default function mergeIncomingAppProjectChanges(opts?: {
  messageOnConflict?: string;
}): ThunkAction<Promise<boolean>, AppProjectEditorRootState, unknown, Action<string>> {
  return async (dispatch, getState) => {
    const state = getState();
    const appId = selectProjectId(state);
    const currentVersion = selectProjectVersion(state);
    if (!appId || !currentVersion) {
      throw new Error('Cannot merge incoming changes for uninitialized editor');
    }
    const editable = selectIsProjectEditable(state);
    if (!editable) {
      // Do not attempt to fetch any changes if project is not editable
      return false;
    }
    // List any versions currently available
    const newer = await listAppProjectPatchHistoryItemsSince(appId, currentVersion)
      // Ignore any errors when fetching patch history items
      .catch(() => []);
    if (!newer.length) {
      return false;
    }
    const newVersion = newer.at(-1)!.reverse_against;
    const upstreamPatch = newer.flatMap((item) => item.forward_patch);
    const prevConflictingVersion = getState().editor.conflictVersion;
    // Rebase the editor project with the new patch
    dispatch(rebaseEditorProject({ version: newVersion, patch: upstreamPatch }));
    const nextConflictingVersion = getState().editor.conflictVersion;
    if (nextConflictingVersion !== prevConflictingVersion && nextConflictingVersion != null) {
      dispatch(
        showNotification(
          'error',
          opts?.messageOnConflict ??
            'Conflict: The app was modified by someone else but your unsaved changes could not be applied on top of them and have been discarded.',
        ),
      );
      return true;
    }
    if (nextConflictingVersion == null) {
      // No conflicts, but ensure that DOLAN hasn't ended up in invalid state
      return dispatch(revertAppProjectIfInvalid());
    }
    return false;
  };
}
